<template>
  <div ref="containerRef" class="virtual-grid-wrapper">
    <div class="virtual-grid-spacer" :style="{ minHeight: `${totalHeight}px` }">
      <div
        class="virtual-grid-content"
        :style="{
          transform: `translateY(${offsetTop}px)`,
          '--columns': columns,
        }"
      >
        <div class="ugc-item" v-for="item in visibleItems" :key="item[itemKey]">
          <slot :item="item"></slot>
        </div>
      </div>
    </div>
  </div>
  <div class="virtual-grid-footer">
    <slot name="footer"></slot>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";

const props = defineProps({
  // 数据列表
  items: {
    type: Array,
    required: true,
    default: () => [],
  },
  // 每个元素的唯一key
  itemKey: {
    type: String,
    default: "ID",
  },
  // 每行的列数
  columns: {
    type: Number,
    default: 4,
  },
  // 每个元素的高度（不包括gap）
  itemHeight: {
    type: Number,
    required: true,
  },
  // 行间距（gap）
  rowGap: {
    type: Number,
    default: 30,
  },
  // 缓冲行数（上下各多渲染几行）
  bufferRows: {
    type: Number,
    default: 3,
  },
});

const emit = defineEmits(["reachBottom"]);

const containerRef = ref(null);
const scrollTop = ref(0);
const containerTop = ref(0); // 容器相对于页面顶部的位置
const actualItemHeight = ref(props.itemHeight); // 实际测量的 item 高度
const actualRowGap = ref(props.rowGap); // 实际测量的 gap

// 计算总行数
const totalRows = computed(() => {
  return Math.ceil(props.items.length / props.columns);
});

// 计算每行的实际高度（包括gap）
const rowHeight = computed(() => {
  return actualItemHeight.value + actualRowGap.value;
});

// 计算总高度（最后一行不需要gap）
const totalHeight = computed(() => {
  if (totalRows.value === 0) return 0;
  // (行数 - 1) * (itemHeight + gap) + itemHeight
  // 使用测量的实际高度，而不是传入的 props
  const height =
    (totalRows.value - 1) * rowHeight.value + actualItemHeight.value;

  return height;
});

// 计算可视区域内的起始和结束行
const visibleRange = computed(() => {
  const viewportHeight = window.innerHeight;

  // 计算相对于容器的滚动位置
  const relativeScrollTop = Math.max(0, scrollTop.value - containerTop.value);

  // 计算可视区域内的起始行（向下取整，减去缓冲区）
  const startRow = Math.max(
    0,
    Math.floor(relativeScrollTop / rowHeight.value) - props.bufferRows
  );

  // 计算可视区域内的结束行（向上取整，加上缓冲区）
  const visibleRows = Math.ceil(viewportHeight / rowHeight.value);
  const endRow = Math.min(
    totalRows.value,
    startRow + visibleRows + props.bufferRows * 2
  );

  return { startRow, endRow };
});

// 计算可视区域内应该渲染的元素
const visibleItems = computed(() => {
  const { startRow, endRow } = visibleRange.value;
  const startIndex = startRow * props.columns;
  const endIndex = Math.min(endRow * props.columns, props.items.length);

  return props.items.slice(startIndex, endIndex);
});

// 计算顶部偏移
const offsetTop = computed(() => {
  return visibleRange.value.startRow * rowHeight.value;
});

// 更新容器位置
const updateContainerPosition = () => {
  if (containerRef.value) {
    const rect = containerRef.value.getBoundingClientRect();
    containerTop.value = rect.top + window.pageYOffset;
  }
};

// 测量实际的元素高度（解决 px2vw 插件导致的高度不一致问题）
const measureActualHeight = () => {
  if (!containerRef.value) return;

  // 等待 DOM 渲染
  setTimeout(() => {
    const allItems = containerRef.value.querySelectorAll(".ugc-item");
    const firstItem = allItems[0];

    if (firstItem && allItems.length > 0) {
      // 测量 item 的实际高度
      actualItemHeight.value = firstItem.offsetHeight;

      // 尝试测量 gap（通过第二行第一个元素的位置计算）
      const secondRowFirstItem = allItems[props.columns];
      if (secondRowFirstItem) {
        const firstRect = firstItem.getBoundingClientRect();
        const secondRect = secondRowFirstItem.getBoundingClientRect();
        actualRowGap.value = secondRect.top - firstRect.bottom;
      }

    }
  }, 100);
};

// 滚动处理（在 window 上监听）
const handleScroll = () => {
  scrollTop.value = window.pageYOffset || document.documentElement.scrollTop;

  // 检查是否接近底部（提前500px触发）
  const scrollHeight = document.documentElement.scrollHeight;
  const clientHeight = window.innerHeight;
  const distanceToBottom = scrollHeight - scrollTop.value - clientHeight;

  if (distanceToBottom < 500) {
    emit("reachBottom");
  }
};

// 防抖更新容器位置
let updateTimer = null;
const handleResize = () => {
  if (updateTimer) clearTimeout(updateTimer);
  updateTimer = setTimeout(() => {
    updateContainerPosition();
  }, 100);
};

onMounted(() => {
  updateContainerPosition();
  scrollTop.value = window.pageYOffset || document.documentElement.scrollTop;

  // 测量实际的元素高度
  measureActualHeight();

  // 监听窗口滚动
  window.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("resize", handleResize);
  if (updateTimer) clearTimeout(updateTimer);
});

// 监听数据变化，重新测量高度
watch(
  () => props.items.length,
  async (newLength, oldLength) => {
    if (newLength > 0 && (oldLength === 0 || newLength === props.columns * 2)) {
      // 初次加载或数据足够时，重新测量
      await nextTick();
      measureActualHeight();
    }
  }
);

// 暴露方法
defineExpose({
  updateContainerPosition,
});
</script>
